import { formatCurrency } from './format'
import { PRODUCT_TYPE } from './product-types'

interface Options {
  wrapperColorVar?: string
  dividerColorVar?: string
}

/**
 * Builds the HTML string used in tooltips to show the monthly cost breakdown
 * for the given plan and applicants.
 */
export function getCostBreakdownDescription(
  plan: any,
  quoteData: any,
  isAgent: boolean,
  options: Options = {}
): string {
  const applicants = quoteData?.quoteApplication?.applicants || []
  const primary = applicants.find(
    (a: any) => a.relationship?.toLowerCase?.() === 'self'
  )
  const spouse = applicants.find(
    (a: any) => a.relationship?.toLowerCase?.() === 'spouse'
  )
  const children = applicants.filter(
    (a: any) => a.relationship?.toLowerCase?.() === 'child'
  )

  // Defensive: deduplicate applicantRate by personId to avoid duplicate rows
  const applicantRateRaw: any[] = Array.isArray(plan?.applicantRate)
    ? plan.applicantRate
    : []
  const applicantRate: any[] = (() => {
    try {
      const byPerson = new Map<string, any>()
      for (const rate of applicantRateRaw) {
        const key = String((rate as any)?.personId ?? '')
        if (!byPerson.has(key)) byPerson.set(key, rate)
      }
      return Array.from(byPerson.values())
    } catch {
      return applicantRateRaw
    }
  })()
  const primaryRate = applicantRate.find(
    (rate: any) => rate.personId === primary?.sequenceId
  )
  const spouseRate = applicantRate.find(
    (rate: any) => rate.personId === spouse?.sequenceId
  )
  const childrenRates = applicantRate.filter((rate: any) =>
    children.some((child: any) => child.sequenceId === rate.personId)
  )

  const lineItemStyle =
    'display: grid; grid-template-columns: 20px 60px auto; align-items: center; gap: 8px;'

  const wrapperColorVar =
    options.wrapperColorVar || 'var(--lh-color-neutral-900, #111313)'
  const dividerColorVar =
    options.dividerColorVar || 'var(--lh-color-neutral-600, #676d70)'

  const wrapperStyle = `color: ${wrapperColorVar}; display: flex; flex-direction: column; gap: 4px; width: 100%; white-space: nowrap;`

  // Determine product type and plan source
  const productType = (() => {
    try {
      const raw = String(plan?.planType || '').toLowerCase()
      if (raw.includes('dental')) return PRODUCT_TYPE.DENTAL
      if (raw.includes('vision')) return PRODUCT_TYPE.VISION

      const lobRaw = String(plan?.lob || (plan?.details?.lob as any) || '')
      const lob = lobRaw.toLowerCase()
      if (lob === 'idtl' || lob.includes('dental')) return PRODUCT_TYPE.DENTAL
      if (lob === 'vis1' || lob.includes('vision')) return PRODUCT_TYPE.VISION

      const nameRaw = String(plan?.description || plan?.planName || '')
      const name = nameRaw.toLowerCase()
      if (name.includes('dental')) return PRODUCT_TYPE.DENTAL
      if (name.includes('vision')) return PRODUCT_TYPE.VISION

      // Strong signal from shape: vision uses planRate, dental uses rate
      if (plan?.planRate != null) return PRODUCT_TYPE.VISION
      if (plan?.rate != null) return PRODUCT_TYPE.DENTAL

      return ''
    } catch {
      return ''
    }
  })()

  // Determine plan source: crossSalePlans (dental), individualPlans (vision), or jsonPlans (dental-only)
  const planSource = (() => {
    try {
      // Check if this is from crossSalePlans (dental plans with vision cross-sale)
      const isCrossSaleDental =
        productType === PRODUCT_TYPE.DENTAL &&
        quoteData?.crossSalePlans?.some((p: any) => p.planId === plan.planId)
      if (isCrossSaleDental) return 'crossSalePlans'

      // Check if this is from individualPlans (vision plans)
      const isIndividualVision =
        productType === PRODUCT_TYPE.VISION &&
        quoteData?.individualPlans?.some(
          (p: any) => p.id === plan.planId || p.planId === plan.planId
        )
      if (isIndividualVision) return 'individualPlans'

      // Check if this is from jsonPlans (dental-only)
      const isJsonDental =
        productType === PRODUCT_TYPE.DENTAL &&
        quoteData?.jsonPlans?.some((p: any) => p.planId === plan.planId)
      if (isJsonDental) return 'jsonPlans'

      return 'unknown'
    } catch {
      return 'unknown'
    }
  })()

  // Get premium value based on plan source
  const premiumValue = (() => {
    try {
      if (planSource === 'crossSalePlans') {
        // Dental plan from crossSalePlans: use rate.monthlyPremium
        return typeof plan?.rate?.monthlyPremium === 'number'
          ? plan.rate.monthlyPremium
          : Number(plan?.rate?.monthlyPremium || 0)
      } else if (planSource === 'individualPlans') {
        // Vision plan from individualPlans: use planRate.monthlyPremium
        return typeof plan?.planRate?.monthlyPremium === 'number'
          ? plan.planRate.monthlyPremium
          : Number(plan?.planRate?.monthlyPremium || 0)
      } else if (planSource === 'jsonPlans') {
        // Dental-only plan from jsonPlans: use rate.monthlyPremium
        return typeof plan?.rate?.monthlyPremium === 'number'
          ? plan.rate.monthlyPremium
          : Number(plan?.rate?.monthlyPremium || 0)
      }
      // Fallback
      return typeof plan?.rate?.monthlyPremium === 'number'
        ? plan.rate.monthlyPremium
        : typeof plan?.planRate?.monthlyPremium === 'number'
          ? plan.planRate.monthlyPremium
          : typeof plan?.monthlyPremium === 'number'
            ? plan.monthlyPremium
            : Number(plan?.monthlyPremium || 0)
    } catch {
      return Number(plan?.monthlyPremium || 0)
    }
  })()

  // Use applicantRate from service response directly - no form selection filtering
  const includeSpouse =
    !!spouseRate &&
    !spouseRate?.memberExcludedFromBillingIndicator &&
    Number(spouseRate?.monthlyPremium) > 0

  const includedChildren = childrenRates.filter(
    (rate: any) =>
      !rate?.memberExcludedFromBillingIndicator &&
      Number(rate?.monthlyPremium) > 0
  )
  const childrenLines = includedChildren.length
    ? includedChildren
        .map((rate: any) => {
          try {
            return `<span style="${lineItemStyle}"><span>+</span><span><strong>${formatCurrency(rate?.monthlyPremium || 0)}</strong></span><span> Your ${isAgent ? "client's " : ''}child</span></span>`
          } catch {
            return `<span style="${lineItemStyle}"><span>+</span><span><strong>${formatCurrency(rate?.monthlyPremium || 0)}</strong></span><span> Your ${isAgent ? "client's " : ''}child</span></span>`
          }
        })
        .join('')
    : ''

  // Compute total as the sum of applicant rates shown in the breakdown
  const primaryAmount = Number(primaryRate?.monthlyPremium || 0)
  const spouseAmount = includeSpouse
    ? Number(spouseRate?.monthlyPremium || 0)
    : 0
  const childrenAmount = includedChildren.reduce(
    (sum: number, rate: any) => sum + Number(rate?.monthlyPremium || 0),
    0
  )
  const applicantSum = primaryAmount + spouseAmount + childrenAmount

  // For immediate-binder or when there are no applicants, show simplified breakdown
  const hasApplicants = applicants.length > 0

  if (!hasApplicants) {
    // No applicants: show just the plan-level premium
    return `<div style="${wrapperStyle}">
              <lh-typography variant="label" size="label-xs">MONTHLY PREMIUM</lh-typography>
              <span style="${lineItemStyle}"><span></span><span><strong>${formatCurrency(premiumValue)}</strong></span><span> Total</span></span>
              <br/>
            </div>`
  }

  // Compute total as the sum of applicant rates shown in the breakdown
  const totalValue = applicantSum > 0 ? applicantSum : premiumValue

  // Avoid wrapping block elements inside a typography <p> container to prevent
  // the first row from being collapsed/hidden in compare tooltips. Use a plain
  // wrapper and span-based rows so the HTML remains valid inside text contexts.
  return `<div style="${wrapperStyle}">
            <lh-typography type="label" size="xxxs">COST BREAKDOWN</lh-typography>
            <span style="${lineItemStyle}"><span>+</span><span><strong>${formatCurrency(primaryRate?.monthlyPremium)}</strong></span><span>You${isAgent ? 'r client' : ''}</span></span>
            ${includeSpouse ? `<span style="${lineItemStyle}"><span>+</span><span><strong>${formatCurrency(spouseRate?.monthlyPremium)}</strong></span><span> Your ${isAgent ? "client's " : ''}spouse/domestic partner</span></span>` : ''}
            ${childrenLines}
            <span style="display:block; border-bottom: 1px solid ${dividerColorVar};"></span>
            <span style="${lineItemStyle}"><span></span><span><strong>${formatCurrency(totalValue)}</strong></span><span> Total</span></span>
            <br/>
          </div>`
}
